import IconAngleBottom from '@assets/icons/angle-bottom.svg?react';
import IconAngleTop from '@assets/icons/angle-top.svg?react';
import { Avatar, Box, Button, Theme } from '@mui/material';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useAuthContext } from '../../../contexts/AuthContext';
import { identityLogoutStart } from '../../../redux/identity/identitySlice';
import UserPopover from './UserPopover';
import UserProfileModal from '../UserProfileModal';
import UserAvatar from './UserAvatar';

const UserWidget = () => {
	const dispatch = useDispatch();
	const { i18n } = useTranslation();
	const languages = i18n?.options?.resources;
	const [profileModalOpen, setProfileModalOpen] = useState(false);
	const [avatarAnchorEl, setAvatarAnchorEl] = useState<null | HTMLElement>(
		null,
	);
	const { loginUser, selfUser, databases } = useAuthContext();
	const containerRef = useRef<HTMLDivElement>(null);
	const handleAvatarClick = (event: React.MouseEvent<HTMLButtonElement>) => {
		setAvatarAnchorEl(containerRef.current);
	};

	const handleAvatarPopoverClose = () => {
		setAvatarAnchorEl(null);
	};

	const handleProfileModalClose = () => {
		setProfileModalOpen(false);
	};

	return (
		<Box
			sx={{ display: 'flex', alignItems: 'center', gap: '4px', order: 3 }}
			ref={containerRef}
		>
			<Button
				id="user-menu-button"
				onClick={handleAvatarClick}
				aria-haspopup="true"
				aria-expanded={Boolean(avatarAnchorEl) ? 'true' : undefined}
				aria-label="open user menu"
				variant="text"
				sx={(theme: Theme) => ({
					display: 'flex',
					alignItems: 'center',
					gap: '12px',
					p: '4px',
					minWidth: 0,
					fontSize: 12,
					color: theme.palette.kemek.darkestGray,
					fontWeight: 'medium',
					textTransform: 'none',
					'& .MuiButton-endIcon': {
						alignSelf: 'flex-start',
						marginTop: { xs: '16px', md: '8px' },
					},
				})}
				// endIcon={}
			>
				<UserAvatar user={selfUser} size={40} />
				<Box
					sx={{
						display: { xs: 'none', md: 'flex' },
						flexDirection: 'column',
						position: 'relative',
						textAlign: 'left',
						pr: '20px',
					}}
				>
					<Box sx={{ fontSize: 12, fontWeight: 500 }}>
						{selfUser?.Name} {selfUser?.Surname}
					</Box>
					<Box
						sx={{
							fontSize: 10,
							color: (theme: Theme) => '#969696', // why?
						}}
					>
						{databases?.find((d) => d.Id === loginUser?.currentlyActiveDb)
							?.Client || '-'}
					</Box>
					<Box sx={{ position: 'absolute', top: 0, right: 0 }}>
						{Boolean(avatarAnchorEl) ? <IconAngleTop /> : <IconAngleBottom />}
					</Box>
				</Box>
			</Button>
			<UserPopover
				open={Boolean(avatarAnchorEl)}
				anchorEl={avatarAnchorEl}
				onClose={handleAvatarPopoverClose}
				onOpenProfile={() => {
					setProfileModalOpen(true);
					handleAvatarPopoverClose();
				}}
				user={loginUser}
				languages={(languages && Object.keys(languages)) || []}
				currentLanguage={i18n.language}
				onLanguageChange={(lng) => {
					i18n.changeLanguage(lng);
					// Structure caches are language-stamped — entries for the old
					// language simply stop matching after the reload.
					handleAvatarPopoverClose();
					window.location.reload();
				}}
				onLogout={() => {
					dispatch(identityLogoutStart());
				}}
			/>
			{profileModalOpen && (
				<UserProfileModal
					open={profileModalOpen}
					onClose={handleProfileModalClose}
				/>
			)}
		</Box>
	);
};

export default UserWidget;
